import BaseNotificationService, { INotification } from "@edaz/services/baseNotificationService";
import { bound } from "@frui.ts/helpers/dist";
import { observable } from "mobx";
import EventBus, { serverError, userLogout } from "./eventBus";
import { scrollToTop } from "utils/scrollTo";
import { IconType } from "@edaz/edazkit/Icon";
import LocalizationService from "./localizationService";

export enum NotificationType {
  INFO = "info",
  WARNING = "warning",
  SUCCESS = "success",
  ERROR = "error",
}

export interface NotificationAction {
  func: () => void;
  link?: string;
  title?: string;
}

export interface Notification extends INotification {
  type: NotificationType;
  permanent?: boolean;
  action?: NotificationAction;
  timeToLive?: number;
  icon?: IconType;
  iconHeight?: number;
  iconWidth?: number;
  contentCenter?: boolean;
}

export default class NotificationService extends BaseNotificationService<Notification> {
  @observable notifications: Map<string, Notification>;

  constructor(private eventBus: EventBus, private localizationService: LocalizationService) {
    super();
    this.eventBus.subscribe(serverError, this.addServerError);
    this.eventBus.subscribe(userLogout, this.clearNotifications);
  }

  @bound
  add(notification: Notification, scope?: string): string {
    scrollToTop();
    const key = super.add(notification, scope);
    if (!notification.permanent) {
      setTimeout(() => this.remove(key), (notification.timeToLive || 30) * 1000);
    }

    return key;
  }

  @bound
  protected addServerError(event: ReturnType<typeof serverError>) {
    const payload = event.payload;
    let errorMessage: string;
    if (payload.type) {
      errorMessage = this.localizationService.translateError(payload.type);
    } else if (payload.message) {
      errorMessage = payload.message;
    } else {
      errorMessage = this.localizationService.translateError("generic");
    }

    const notification = {
      title: errorMessage,
      type: NotificationType.ERROR,
      icon: IconType.Offline,
    };

    if (Array.from(this.notifications.values()).find(value => value.type === notification.type)) return;

    this.add(notification);
  }

  @bound
  protected clearNotifications() {
    this.notifications.clear();
  }
}
